import React from "react";
import {
  Box,
  Stack,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibraryOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import {
  updateCompany,
  uploadCompanyPhotos,
  deleteCompanyPhoto,
} from "../companiesSlice";
import CompanyFormFields, {
  emptyValues,
  toDateInput,
  normalizePayload,
} from "../CompanyFormFields";
import PhotoSection from "../../../components/common/PhotoSection";

const validation = Yup.object({
  name: Yup.string().trim().required("Name is required"),
});

export default function CompanyDetailsTab() {
  const { company } = useOutletContext();
  const dispatch = useDispatch();
  const { saving, statuses, uploadingPhotos } = useSelector((s) => s.companies);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...emptyValues,
      ...company,
      installation_date: toDateInput(company.installation_date),
      expiry_date: toDateInput(company.expiry_date),
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      const res = await dispatch(
        updateCompany({ id: company._id, payload: normalizePayload(values) })
      );
      if (updateCompany.fulfilled.match(res)) {
        toast.success("Company updated");
      } else {
        toast.error(res.payload || "Save failed");
      }
    },
  });

  const handleUpload = async (type, files) => {
    const res = await dispatch(
      uploadCompanyPhotos({ id: company._id, type, files })
    );
    if (uploadCompanyPhotos.fulfilled.match(res)) {
      toast.success(`${files.length} photo(s) uploaded`);
    } else {
      toast.error(res.payload || "Upload failed");
    }
  };

  const handleRemove = async (type, filename) => {
    const res = await dispatch(
      deleteCompanyPhoto({ id: company._id, type, filename })
    );
    if (deleteCompanyPhoto.fulfilled.match(res)) {
      toast.success("Photo removed");
    } else {
      toast.error(res.payload || "Remove failed");
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: "10px" }}
      >
        <CompanyFormFields formik={formik} statuses={statuses} />
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, sm: 3 }, mb: 12, borderRadius: "10px" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mb: 2,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              backgroundColor: "primary.light",
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PhotoLibraryIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "text.primary",
              fontSize: "0.75rem",
            }}
          >
            Site Photos
          </Typography>
        </Stack>
        <Stack spacing={3}>
          <PhotoSection
            title="Before Photos"
            folder="before_photos"
            existing={company.before_photos || []}
            newFiles={[]}
            addFiles={(files) => handleUpload("before", files)}
            removeExisting={(name) => handleRemove("before", name)}
            uploading={uploadingPhotos}
          />
          <PhotoSection
            title="After Photos"
            folder="after_photos"
            existing={company.after_photos || []}
            newFiles={[]}
            addFiles={(files) => handleUpload("after", files)}
            removeExisting={(name) => handleRemove("after", name)}
            uploading={uploadingPhotos}
          />
        </Stack>
      </Paper>

      {/* Sticky save bar — pinned to the viewport bottom so it never overlaps
          form content. Adds a subtle shadow to separate from the page. */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: { xs: 0, md: 240 },
          right: 0,
          px: { xs: 1.5, md: 3 },
          py: 1.25,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          boxShadow: "0 -4px 12px -6px rgba(15,23,42,0.10)",
          zIndex: (t) => t.zIndex.appBar - 1,
        }}
      >
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !formik.dirty}
            startIcon={
              saving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveIcon fontSize="small" />
              )
            }
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </Stack>
      </Box>
    </form>
  );
}
